"use client";

import { JumplistNode, useJumplist } from "@faceless-ui/jumplist";
import HomepageCode from "components/ComponentCustomJSON/HomepageCode";
import HomepageDatasource from "components/ComponentCustomJSON/HomepageDatasource";
import HomepageWidgets from "components/ComponentCustomJSON/HomepageWidgets";
import {
  AssetFragment,
  ComponentCustomJsonMetaFragment,
  ComponentHeadingFragment,
  CtaFragment,
} from "contentful/gql/graphql";
import { useRef, useState, useLayoutEffect, useEffect, useCallback, memo, useMemo } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import clsx from "clsx";
import typography from "components/typography";
import "./Jumplist.css";
import HomepageDeploy from "components/ComponentCustomJSON/HomepageDeploy";

const Jumplist = memo(function Jumplist({
  json,
  componentHeading,
  assets,
}: {
  json: {
    tabs: { title: string; refAssetId: string; refHeadingComponent: string; componentType: string }[];
    ctaButton: { ctaButtonLink: string; ctaButtonText: string };
  };
  componentHeading: (ComponentHeadingFragment | undefined)[] | undefined;
  assets: (AssetFragment | undefined)[] | undefined;
}) {
  const jumplist = useJumplist();
  const sectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const headerHeightRef = useRef(54);
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);
  const headerTlRef = useRef<gsap.core.Timeline | null>(null);
  const progressRef = useRef(0);
  const isInitializedRef = useRef(false);

  // Memoize stable values to prevent unnecessary re-renders
  const stableTabs = useMemo(() => json.tabs, [json.tabs]);

  const jumpListScrollToIndex = useCallback(
    ({ index, id }: { index: number; id: string }) => {
      if (jumplist?.scrollToID) {
        jumplist.scrollToID(id);
      }
    },
    [jumplist],
  );

  useLayoutEffect(() => {
    if (isInitializedRef.current) return;

    let mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      gsap.registerPlugin(ScrollTrigger);

      // Create a timeline for the header animation
      headerTlRef.current = gsap
        .timeline({ paused: true })
        .fromTo(
          headingRef.current,
          { opacity: 0, y: -20, visibility: "hidden" },
          { opacity: 1, y: 0, visibility: "visible", duration: 0.4, ease: "power2.out" },
        );

      scrollTriggerRef.current = ScrollTrigger.create({
        trigger: sectionRef.current,
        start: `top top+=${headerHeightRef.current}px`,
        end: "bottom bottom-=900px",
        pin: headingRef.current,
        pinSpacing: false,
        onEnter: () => {
          if (headingRef.current && headerTlRef.current) {
            headerTlRef.current.progress(0).play();
          }
        },
        onUpdate: (self) => {
          progressRef.current = parseFloat(self.progress.toFixed(3));
          if (headingRef.current) {
            headingRef.current.style.setProperty("--progress", progressRef.current.toString());
          }
        },
        onLeaveBack: () => {
          if (headingRef.current && headerTlRef.current) {
            headerTlRef.current.progress(1).reverse();
          }
        },
        onLeave: () => {
          if (headingRef.current && headerTlRef.current) {
            headerTlRef.current.progress(1).reverse();
          }
        },
        onEnterBack: () => {
          if (headingRef.current && headerTlRef.current) {
            headerTlRef.current.progress(0).play();
          }
        },
      });

      isInitializedRef.current = true;

      return () => {
        if (headerTlRef.current) {
          headerTlRef.current.kill();
        }
        if (scrollTriggerRef.current) {
          scrollTriggerRef.current.kill();
        }
      };
    });

    return () => {
      mm.revert();
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
      }
      isInitializedRef.current = false;
    };
  }, []); // Empty dependency array to run only once

  useEffect(() => {
    const updateHeaderHeight = () => {
      const globalHeader = document.querySelector("header#global-header");
      if (globalHeader) {
        const height = globalHeader.getBoundingClientRect().height;
        headerHeightRef.current = height;

        if (scrollTriggerRef.current) {
          scrollTriggerRef.current.vars.start = `top top+=${height}px`;
          scrollTriggerRef.current.refresh();
        }
      }
    };

    // Initial update
    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    const globalHeader = document.querySelector("header#global-header");
    if (globalHeader) {
      resizeObserver.observe(globalHeader);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []); // Empty dependency array to run only once

  const renderedTabs = useMemo(
    () =>
      stableTabs.map((tab, index) => {
        const { refAssetId, refHeadingComponent, componentType } = tab;

        const asset = assets?.find((asset) => asset?.sys.id === refAssetId);
        const heading = componentHeading?.find((heading) => heading?.sys.id === refHeadingComponent);

        switch (componentType) {
          case "homepage-datasources":
            return (
              <div key={`node-${index}`}>
                <JumplistNode key={`node-${index}`} nodeID={`node-${index}`}>
                  <HomepageDatasource componentHeading={heading} assets={assets} />
                </JumplistNode>
              </div>
            );
          case "homepage-widgets":
            return (
              <div key={`node-${index}`}>
                <JumplistNode key={`node-${index}`} nodeID={`node-${index}`}>
                  <HomepageWidgets componentHeading={heading} assets={assets} />
                </JumplistNode>
              </div>
            );
          case "homepage-code":
            return (
              <div key={`node-${index}`}>
                <JumplistNode key={`node-${index}`} nodeID={`node-${index}`}>
                  <HomepageCode componentHeading={heading} assets={assets} />
                </JumplistNode>
              </div>
            );
          case "homepage-deploy":
            return (
              <div key={`node-${index}`}>
                <JumplistNode key={`node-${index}`} nodeID={`node-${index}`}>
                  <HomepageDeploy componentHeading={heading} assets={assets} />
                </JumplistNode>
              </div>
            );
          default:
            return null;
        }
      }),
    [stableTabs, componentHeading, assets],
  );

  return (
    <div ref={sectionRef} className="container relative mx-auto overflow-x-hidden">
      <div
        className="!left-0 z-20 hidden w-screen overflow-x-hidden bg-primary-dark-900 md:block"
        ref={headingRef}
        style={{ visibility: "hidden" }}
        id="jumplist-pin-spacer"
      >
        <div className="flex flex-row justify-evenly gap-4 border-b-2 border-primary-neutral-200/40">
          {stableTabs.map((tab, index) => {
            return (
              <div
                key={`tab-${index}`}
                onClick={() => {
                  jumpListScrollToIndex({ index, id: `node-${index}` });
                }}
                className={clsx(
                  typography.label,
                  "relative z-10 cursor-pointer rounded-md py-xs font-display text-sm font-bold text-primary-dark-100 transition-colors duration-300 hover:text-primary-light-500 xl:text-sm",
                  `[&:has(+ div[style*="--progress"])]:text-primary-light-500`,
                )}
              >
                {tab.title}
              </div>
            );
          })}
          <div
            className="absolute left-0 top-0 h-full border-b-2 border-primary-light-500 transition-transform duration-300 ease-in-out will-change-transform"
            style={{
              width: `calc(100% * var(--progress, 0))`,
            }}
          ></div>
        </div>
      </div>
      {renderedTabs}
    </div>
  );
});

export default Jumplist;
